// src/components/features/chat/LabTestPaymentCard.jsx
//
// 15 Jul 2026 — new. Renders when the chat's order_lab_test create_order
// action resolves — clicking the card opens Razorpay's standard Checkout
// modal (confirmed with Deepu: the actual generic Razorpay UI, not a custom
// panel or embedded screen — see toolRouter.js's handleOrderLabTest
// comments for why). The card itself, not the tool call, is what triggers
// payment; the actual Thyrocare order is only created after a verified
// payment succeeds (src/utils/labTestPayment.js).
//
// 27 Jul 2026 — added a wallet payment option alongside the existing full
// button. Fetches the patient's wallet balance on mount (walletAPI, the
// axios instance from services/api.js — never raw fetch, per
// FIGMA_TO_CODE_CHECKLIST.md); if it's above 0, shows a second button that
// runs the wallet-combined checkout instead of a straight Razorpay charge.

import React, { useState, useEffect } from 'react';
import { payAndCreateLabTestOrder, payAndCreateLabTestOrderWithWallet } from '../../../utils/labTestPayment';
import { walletAPI } from '../../../services/api';
import { applySavedAddressToOrderPayload, formatAddress } from '../../../utils/labOrderIdentity';
import Icon from '../../Icon';

const C = {
  navy:       'var(--brand-dark)',
  gold:       'var(--amber-text)',
  goldLight:  'var(--amber-bg)',
  goldBorder: 'var(--amber)',
  white:      'var(--bg-surface)',
  textPrimary:'var(--text-primary)',
  textMuted:  'var(--text-muted)',
  good:       'var(--green)',
  goodTint:   'var(--green-bg)',
  bad:        'var(--red)',
  badTint:    'var(--red-bg)',
};

function formatRupees(paise) {
  if (paise == null) return '—';
  return `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;
}

// Review-before-you-pay. `summary` (patient name, phone, email, full address, slot, tests) is drawn here, in
// the patient's browser, from their own profile — the chat assistant never receives those details, it only
// points at this card. `savedAddresses` are the patient's saved addresses at the SAME pincode as the booked
// slot (the only ones the slot is valid for), so the address can be switched here without re-booking.
export default function LabTestPaymentCard({
  patient, panelName, panelCode, testCode, amountPaise, orderPayload,
  summary: initialSummary, savedAddresses = [], selectedAddressId = null,
}) {
  const [payload, setPayload] = useState(orderPayload);
  const [summary, setSummary] = useState(initialSummary);
  const [addressId, setAddressId] = useState(selectedAddressId);
  const [status, setStatus]   = useState('idle'); // idle | processing | success | failed
  const [message, setMessage] = useState('');
  const [orderRef, setOrderRef] = useState(null);
  const [walletBalanceMinorUnits, setWalletBalanceMinorUnits] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (patient?.id) {
      walletAPI.getBalance(patient.id)
        .then((res) => { if (!cancelled) setWalletBalanceMinorUnits(res.data.balanceMinorUnits || 0); })
        .catch(() => { /* silent — wallet option just won't show if this fails */ });
    }
    return () => { cancelled = true; };
  }, [patient?.id]);

  const onAddressChange = (id) => {
    const saved = savedAddresses.find((a) => String(a.id) === String(id));
    if (!saved) return;
    setPayload((prev) => applySavedAddressToOrderPayload(prev, saved));
    setSummary((prev) => ({ ...prev, addressLine: formatAddress(saved) }));
    setAddressId(saved.id);
  };

  const runPayment = async (usingWallet) => {
    if (status === 'processing' || status === 'success') return;
    setStatus('processing');
    setMessage('');

    const payFn = usingWallet ? payAndCreateLabTestOrderWithWallet : payAndCreateLabTestOrder;
    const result = await payFn({
      patientId:    patient?.id,
      panelCode,
      testCode,
      panelName,
      patientName:  `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
      patientEmail: patient?.email,
      patientPhone: patient?.phone,
      orderPayload: payload,
    });

    if (result.success) {
      setStatus('success');
      setOrderRef(result.order?.orderId || result.order?.refOrderNo || null);
      return;
    }

    setStatus('failed');
    if (result.reason === 'payment_cancelled') {
      setMessage('Payment was not completed. Click to try again.');
    } else if (result.reason === 'payment_failed') {
      setMessage(`Payment failed: ${result.message || 'please try again.'}`);
    } else if (result.reason === 'order_creation_failed') {
      // Payment DID succeed here — do not invite the patient to just click
      // and pay again. See labTestPayment.js's own comment on this case.
      setMessage(result.message);
    } else {
      setMessage(result.message || 'Something went wrong. Click to try again.');
    }
  };

  const clickable = status === 'idle' || status === 'failed';
  const showWalletOption = clickable && walletBalanceMinorUnits > 0;

  const canEditAddress = clickable && savedAddresses.length > 1;

  return (
    <div style={{ maxWidth: 340 }}>
      <div
        style={{
          border: `1.5px solid ${status === 'success' ? C.good : status === 'failed' ? C.bad : C.goldBorder}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: C.white,
          boxShadow: '0 1px 3px rgba(13,30,53,0.08)',
        }}
      >
        <div style={{ background: C.navy, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="vial" size={16} />
          <span style={{ color: C.white, fontSize: 12, fontWeight: 600, letterSpacing: 0.3 }}>
            LAB TEST ORDER
          </span>
        </div>

        <div style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 4 }}>
            {panelName || panelCode || 'Lab Panel'}
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 12 }}>
            {formatRupees(amountPaise)}
          </div>

          {summary && (
            <div style={{ marginBottom: 12 }} data-testid="order-review">
              <div className="field-group">
                <div className="field-label">Patient</div>
                <p className="field-text">{summary.patientName || '—'}</p>
              </div>
              <div className="field-group">
                <div className="field-label">Contact</div>
                <p className="field-text">{[summary.phone, summary.email].filter(Boolean).join(' · ') || '—'}</p>
              </div>
              <div className="field-group">
                <div className="field-label">Collection address</div>
                <p className="field-text">{summary.addressLine || '—'}</p>
                {canEditAddress && (
                  <select
                    className="form-select"
                    aria-label="Choose a saved address"
                    value={addressId ?? ''}
                    onChange={(e) => onAddressChange(e.target.value)}
                  >
                    {savedAddresses.map((a) => (
                      <option key={a.id} value={a.id}>
                        {[a.addressType, a.street].filter(Boolean).join(' — ')}
                      </option>
                    ))}
                  </select>
                )}
                {clickable && (
                  <p className="field-text">
                    Need a different pincode? Tell the assistant — slots are booked per pincode.
                  </p>
                )}
              </div>
              <div className="field-group">
                <div className="field-label">Date and time</div>
                <p className="field-text">{[summary.date, summary.startTime].filter(Boolean).join(' at ') || '—'}</p>
              </div>
              {summary.items?.length > 0 && (
                <div className="field-group">
                  <div className="field-label">Tests</div>
                  <p className="field-text">{summary.items.join(', ')}</p>
                </div>
              )}
            </div>
          )}

          {status === 'success' ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 8, background: C.goodTint,
              fontSize: 12, fontWeight: 600, color: C.good,
            }}>
              ✅ Order confirmed{orderRef ? ` — Ref ${orderRef}` : ''}
            </div>
          ) : (
            <>
              <div
                role="button"
                tabIndex={clickable ? 0 : -1}
                onClick={clickable ? () => runPayment(false) : undefined}
                onKeyDown={(e) => {
                  if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); runPayment(false); }
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '8px 12px', borderRadius: 8,
                  background: status === 'failed' ? C.badTint : C.goldLight,
                  border: `1px solid ${status === 'failed' ? C.bad : C.goldBorder}`,
                  fontSize: 12, fontWeight: 600,
                  color: status === 'failed' ? C.bad : C.gold,
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                {status === 'processing' ? '⏳ Processing…' : status === 'failed' ? '⚠ Retry payment' : '💳 Pay & Book Test'}
              </div>

              {showWalletOption && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => runPayment(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); runPayment(true); }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '8px 12px', borderRadius: 8, marginTop: 8,
                    background: C.white, border: `1px solid ${C.navy}`,
                    fontSize: 12, fontWeight: 600, color: C.navy, cursor: 'pointer',
                  }}
                >
                  👛 Pay with wallet ({formatRupees(walletBalanceMinorUnits)} available)
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {status === 'failed' && message && (
        <div style={{ marginTop: 6, fontSize: 11, color: C.bad }}>{message}</div>
      )}
    </div>
  );
}
